import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import {
  EMAILJS_PUBLIC_KEY,
  EMAILJS_SERVICE_ID,
  TEMPLATE_IDS,
  initializeEmailJS
} from '../html-email/emailjs-config';
import BMNLogo from '../img/BMNLogo.png';
import nimg1 from '../img/NIMG1N.svg';

const MAKE_WEBHOOK_URL = 'https://hook.eu2.make.com/7pynsgbax6gyoslgr58hqa67ag816wm5';
const META_PIXEL_ID = '1224085769883137';

const ClaimReview = () => {
  // --- State Management ---
  const [step, setStep] = useState(0); // 0: Landing, 1-5: Questions, 6: Spinner, 7: Eligibility Success, 8: Contact Form, 9: Not Eligible
  const [answers, setAnswers] = useState({
    involved: null,
    injured: null,
    fault: null,
    attorney: null,
    time: null
  });

  // Contact form state for Step 5
  const [contactForm, setContactForm] = useState({
    fullName: '',
    phone: '',
    email: '',
  });
  const [crmSubmissionId, setCrmSubmissionId] = useState(null);
  const [hasPhoneSubmitted, setHasPhoneSubmitted] = useState(false);
  const [lastEmailUpdate, setLastEmailUpdate] = useState('');
  const [lastFetchedSubmission, setLastFetchedSubmission] = useState(null);

  // Ref to prevent multiple simultaneous API calls
  const isProcessingPhoneRef = React.useRef(false);


  // Unique session ID for tracking this form submission
  const [uniqueSessionId] = useState(() => {
    // Generate a unique ID: timestamp + random string
    return `CR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  });

  const [contactErrors, setContactErrors] = useState({
    fullName: '',
    phone: '',
    email: '',
  });

  // Email submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState({
    admin: null, // null, 'sending', 'success', 'error'
    user: null,
    message: ''
  });

  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState({
    type: 'success', // 'success', 'warning', 'error'
    title: '',
    message: '',
    details: []
  });

  // Responsive State for Inline Styles
  const [isMobile, setIsMobile] = useState(false);

  // --- Effects ---
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Load Meta Pixel script if not already loaded
    if (!window.fbq) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);

      window.fbq = function () {
        window.fbq.callMethod
          ? window.fbq.callMethod.apply(window.fbq, arguments)
          : window.fbq.queue.push(arguments);
      };
      window.fbq.queue = [];
      window.fbq.loaded = true;
      window.fbq.version = '2.0';
    }

    // Init pixel ONCE
    if (!window.__META_PIXEL_INIT__) {
      window.fbq('init', META_PIXEL_ID);
      window.fbq('track', 'PageView');
      window.__META_PIXEL_INIT__ = true;
      console.log('✅ Meta Pixel initialized & PageView fired');
    }
  }, []);


  // Initialize EmailJS on component mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Load EmailJS if not already loaded
      if (!window.emailjs) {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js';
        script.async = true;
        script.onload = () => {
          initializeEmailJS();
        };
        document.head.appendChild(script);
      } else {
        initializeEmailJS();
      }
    }
  }, []);

  // Handle Window Resize for Responsive Layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    // Initial check
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle Eligibility Logic
  useEffect(() => {
    if (step === 6) {
      const timer = setTimeout(() => {
        // Eligibility rules from spreadsheet:
        // 1. Were you involved? Yes = Eligible, No = Not eligible
        // 2. Were you injured? Yes = Eligible, No = Not eligible
        // 3. Were you legally at fault? Yes = Not eligible, No = Eligible
        // 4. Do you already have an attorney? Yes = Not eligible, No = Eligible
        // 5. When did the accident happen? All time periods = Eligible

        const isEligible =
          answers.involved === 'yes' &&  // Must be involved
          answers.injured === 'yes' &&   // Must be injured
          answers.fault === 'no' &&      // Must NOT be at fault
          answers.attorney === 'no' &&   // Must NOT have an attorney
          answers.time !== null;         // Time period must be selected (all are eligible)

        if (isEligible) {
          setStep(7); // Eligibility Success Screen
        } else {
          setStep(9); // Not Eligible
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [step, answers]);

  // Auto-navigate from eligibility success screen to form after 2 seconds
  useEffect(() => {
    if (step === 7) {
      const timer = setTimeout(() => {
        setStep(8); // Navigate to contact form
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [step]);

  // Track Submit-Lead Form Event when form is successfully submitted
  useEffect(() => {
    if (submissionStatus.admin === 'success' && submissionStatus.user === 'success') {
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17811188696/i4gpCMzRhuIbENjXhK1C',
          'value': 1.0,
          'currency': 'USD'
        });
      }
    }
  }, [submissionStatus.admin, submissionStatus.user]);



  // --- Handlers ---

  const getTrustedFormCertUrlFromDOM = () => {
    if (typeof document === 'undefined') return '';
    const certField = document.querySelector('[name="xxTrustedFormCertUrl"]');
    return certField?.value || '';
  };

  const getIPAddress = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data?.ip || '';
    } catch (error) {
      console.error('Failed to get IP address:', error);
      return '';
    }
  };

  const sendToMakeWebhook = async ({ name, email, phone, trustedFormCertUrl, ipAddress }) => {
    try {
      const res = await fetch(MAKE_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          phone,
          trustedFormCertUrl,
          ipAddress,
        }),
      });

      if (!res.ok) {
        console.error('Make webhook failed:', res.status, res.statusText);
      }
    } catch (err) {
      console.error('Make webhook error:', err);
    }
  };

  const sendToCRMBackend = async (formData, { method = 'POST', id = null } = {}) => {
    try {
      // Add unique session ID to form data
      const dataWithUniqueId = {
        ...formData,
        uniqueSessionId: uniqueSessionId,
        timestamp: new Date().toISOString()
      };

      const payload = {
        countryName: 'USA',
        brandName: 'Claim4Accidents',
        websiteName: window.location.hostname || 'claim4accidents.com',
        formname: 'ClaimReview',
        data: dataWithUniqueId
      };

      const url = id
        ? `https://crm-internal-backend-ayb9fqawg8b6bjen.canadacentral-01.azurewebsites.net/api/submitformdata/${id}`
        : 'https://crm-internal-backend-ayb9fqawg8b6bjen.canadacentral-01.azurewebsites.net/api/submitformdata';

      console.log(`[CRM] Making ${method} request to:`, url, { id, hasId: !!id });

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        console.error(`[CRM] ${method} request failed:`, res.status, res.statusText);
        const errorText = await res.text().catch(() => '');
        console.error('[CRM] Error response:', errorText);
        return null;
      }

      const json = await res.json().catch(() => ({}));
      console.log(`[CRM] ${method} request successful:`, json);
      return json;
    } catch (err) {
      console.error(`[CRM] ${method} request error:`, err);
      return null;
    }
  };

  const fetchCrmSubmission = async (id) => {
    if (!id) {
      console.warn('[CRM] Cannot fetch: No ID provided');
      return null;
    }
    try {
      console.log('[CRM] Making GET call to fetch entry with ID:', id);
      const res = await fetch(`https://crm-internal-backend-ayb9fqawg8b6bjen.canadacentral-01.azurewebsites.net/api/submitformdata/id/${id}`);
      if (!res.ok) {
        console.error('[CRM] GET request failed:', res.status, res.statusText);
        return null;
      }
      const json = await res.json().catch(() => null);
      if (json?.data) {
        setLastFetchedSubmission(json.data);
        console.log('[CRM] GET successful! Entry data:', json.data);
      }
      return json?.data || null;
    } catch (err) {
      console.error('[CRM] GET request error:', err);
      return null;
    }
  };

  const triggerPhoneSubmission = async (digits) => {
    // Prevent multiple calls: check ref, state, and exact length
    if (isProcessingPhoneRef.current || hasPhoneSubmitted || !digits || digits.length !== 10) {
      return;
    }

    // Set processing flag immediately to prevent race conditions
    isProcessingPhoneRef.current = true;

    try {
      console.log('[CRM] Phone has 10 digits, making POST call to create entry...');

      const response = await sendToCRMBackend({
        name: contactForm.fullName,
        phone: digits,
        email: contactForm.email || '',
        involved: answers.involved,
        injured: answers.injured,
        fault: answers.fault,
        attorney: answers.attorney,
        period: answers.time,
        tcpaConsent: false,
      }, { method: 'POST' });

      if (response?.id) {
        const uniqueId = response.id;
        console.log('[CRM] POST successful! Unique ID received:', uniqueId);

        // Store the unique ID
        setCrmSubmissionId(uniqueId);
        setHasPhoneSubmitted(true);

        // GET the same entry using the unique ID
        console.log('[CRM] Fetching entry with GET call using unique ID:', uniqueId);
        const fetchedData = await fetchCrmSubmission(uniqueId);

        if (fetchedData) {
          console.log('[CRM] GET successful! Entry retrieved:', fetchedData);
        } else {
          console.warn('[CRM] GET failed - could not retrieve entry');
        }
      } else {
        console.error('[CRM] POST failed - no ID received in response:', response);
      }
    } finally {
      // Always reset the processing flag
      isProcessingPhoneRef.current = false;
    }
  };

  const triggerEmailUpdate = async (emailValue) => {
    const trimmed = emailValue.trim();
    if (!trimmed.toLowerCase().endsWith('.com')) return;
    if (trimmed === lastEmailUpdate) return;

    console.log('[CRM] Email ends with .com, checking for submission ID...');

    // Ensure we have a submission to update; if not, attempt to create using phone
    if (!crmSubmissionId && contactForm.phone.length === 10 && !hasPhoneSubmitted) {
      console.log('[CRM] No submission ID found, creating entry with phone first...');
      await triggerPhoneSubmission(contactForm.phone);
      // Wait a bit for the submission to be created and ID to be stored
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    if (!crmSubmissionId) {
      console.warn('[CRM] Cannot update email: No submission ID available. Phone may not have been submitted yet.');
      return;
    }

    console.log('[CRM] Making PUT call to update email in entry with unique ID:', crmSubmissionId);

    const response = await sendToCRMBackend({
      name: contactForm.fullName,
      phone: contactForm.phone,
      email: trimmed,
      involved: answers.involved,
      injured: answers.injured,
      fault: answers.fault,
      attorney: answers.attorney,
      period: answers.time,
      tcpaConsent: true,
    }, { method: 'PUT', id: crmSubmissionId });

    if (response) {
      setLastEmailUpdate(trimmed);
      console.log('[CRM] PUT successful! Email updated in entry ID:', crmSubmissionId);

      // Optionally fetch the updated entry to verify
      const updatedData = await fetchCrmSubmission(crmSubmissionId);
      if (updatedData) {
        console.log('[CRM] Verified updated entry:', updatedData);
      }
    } else {
      console.error('[CRM] PUT failed - email update unsuccessful');
    }
  };

  const handleAnswer = (key, value) => {
    // Map of question keys to their corresponding steps
    const questionStepMap = {
      'involved': 1,
      'injured': 2,
      'fault': 3,
      'attorney': 4,
      'time': 5
    };

    const expectedStep = questionStepMap[key];

    // Only process and advance if we're on the correct step for this question
    if (step !== expectedStep) {
      return; // Don't process if we're not on the correct step
    }

    // Always update the state, even if the value is the same
    // This ensures clicking the same option again still works
    setAnswers(prev => {
      const newAnswers = { ...prev, [key]: value };
      return newAnswers;
    });

    // Use the expectedStep value to ensure we advance correctly
    setTimeout(() => {
      setStep(prev => {
        // Only advance if we're still on the expected step (prev should equal expectedStep)
        if (prev === expectedStep && prev < 5) {
          return prev + 1;
        } else if (prev === expectedStep && prev === 5) {
          return 6; // Spinner step
        }
        return prev; // Don't change step if we're not on the expected step
      });
    }, 300);
  };

  const resetForm = () => {
    setAnswers({ involved: null, injured: null, fault: null, attorney: null, time: null });
    setStep(0);
    setCrmSubmissionId(null);
    setHasPhoneSubmitted(false);
    setLastEmailUpdate('');
    setLastFetchedSubmission(null);
    isProcessingPhoneRef.current = false; // Reset processing flag
  };

  // --- Validation Helpers for Step 5 Contact Form ---
  const validateFullName = (name) => {
    if (!name) return false;
    const trimmed = name.trim();
    // Only letters, spaces, hyphens and apostrophes, at least 2 characters
    return /^[A-Za-z' -]{2,}$/.test(trimmed);
  };

  const validatePhone = (value) => {
    if (!value) return false;
    // Exactly 10 digits
    return /^\d{10}$/.test(value);
  };

  const validateEmail = (value) => {
    if (!value) return false;
    // Basic email format check that includes "@"
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    const newErrors = { fullName: '', phone: '', email: '' };

    if (!validateFullName(contactForm.fullName)) {
      newErrors.fullName = 'Please enter a valid name using letters only (no numbers).';
    }

    if (!validatePhone(contactForm.phone)) {
      newErrors.phone = 'Phone number must be exactly 10 digits and contain numbers only.';
    }

    if (!validateEmail(contactForm.email)) {
      newErrors.email = 'Please enter a valid email address that includes "@".';
    }

    setContactErrors(newErrors);

    const isValid = !newErrors.fullName && !newErrors.phone && !newErrors.email;
    if (!isValid) return;

    // Check TCPA consent (checkbox should be checked)
    const tcpaCheckbox = document.getElementById('tcpa');
    if (!tcpaCheckbox || !tcpaCheckbox.checked) {
      setDialogContent({
        type: 'error',
        title: 'Consent Required',
        message: 'Please agree to the Privacy Policy and Terms of Use to continue.',
        details: []
      });
      setShowDialog(true);
      return;
    }

    // Start email submission process
    setIsSubmitting(true);
    setSubmissionStatus({
      admin: 'sending',
      user: null,
      message: 'Sending your information...'
    });

    try {
      // Prepare form data
      const formData = {
        fullName: contactForm.fullName,
        phone: contactForm.phone,
        email: contactForm.email,
        involved: answers.involved,
        injured: answers.injured,
        fault: answers.fault,
        attorney: answers.attorney,
        time: answers.time,
        tcpaConsent: true
      };

      // Send admin email first
      if (typeof window === 'undefined' || !window.emailjs) {
        throw new Error('EmailJS is not available. Please refresh the page and try again.');
      }

      // Initialize EmailJS if needed
      if (!window.emailjs.init) {
        window.emailjs.init(EMAILJS_PUBLIC_KEY);
      }

      // Format template variables
      const currentDate = new Date();
      const isEligible =
        answers.involved === 'yes' &&
        answers.injured === 'yes' &&
        answers.fault === 'no' &&
        answers.attorney === 'no' &&
        answers.time !== null;
      const phoneNumber = contactForm.phone.replace(/\D/g, '').slice(0, 10);
      const fullPageUrl = window.location.href;

      const trustedFormCertUrl = getTrustedFormCertUrlFromDOM();
      const ipAddress = await getIPAddress();

      await sendToMakeWebhook({
        name: contactForm.fullName,
        email: contactForm.email,
        phone: phoneNumber,
        trustedFormCertUrl,
        ipAddress,
      });

      const adminTemplateParams = {
        full_name: contactForm.fullName,
        phone_number: phoneNumber,
        email: contactForm.email,
        involved_in_accident: answers.involved === 'yes' ? 'Yes' : (answers.involved === 'no' ? 'No' : 'Not specified'),
        injured_hospitalized: answers.injured === 'yes' ? 'Yes' : (answers.injured === 'no' ? 'No' : 'Not specified'),
        legally_at_fault: answers.fault === 'yes' ? 'Yes' : (answers.fault === 'no' ? 'No' : 'Not specified'),
        have_attorney: answers.attorney === 'yes' ? 'Yes' : (answers.attorney === 'no' ? 'No' : 'Not specified'),
        accident_timing: answers.time || 'Not specified',
        eligibility_status: isEligible ? 'eligible' : 'not-eligible',
        eligibility_status_text: isEligible ? 'Eligible' : 'Not Eligible',
        tcpa_consent: 'Yes',
        full_page_url: fullPageUrl,
        ip_address: ipAddress || 'N/A',
        submission_date: currentDate.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        submission_time: currentDate.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        })
      };

      // Send admin email
      setSubmissionStatus({
        admin: 'sending',
        user: null,
        message: 'Sending admin notification...'
      });

      const adminResult = await emailjs.send(
        EMAILJS_SERVICE_ID,
        TEMPLATE_IDS.CLAIM_REVIEW_ADMIN,
        adminTemplateParams,
        EMAILJS_PUBLIC_KEY
      );

      if (adminResult.status === 200) {
        setSubmissionStatus({
          admin: 'success',
          user: 'sending',
          message: 'Admin email sent successfully! Sending confirmation email...'
        });

        // Admin email successful, now send user email
        // Include all possible email field names that EmailJS might expect
        // Ensure email is lowercase and trimmed for EmailJS compatibility
        const userEmail = contactForm.email.trim().toLowerCase();
        const userTemplateParams = {
          // Standard EmailJS fields - to_email is critical and must be lowercase
          to_email: userEmail, // Primary recipient email (must be lowercase)
          email: userEmail, // Alternative field name
          user_email: userEmail, // Another alternative
          to_name: contactForm.fullName || 'Valued Customer', // Recipient name
          from_name: 'Claim4Accident', // Sender name
          from_email: 'reachus@fightformesothelioma.com', // Sender email
          subject: 'Thank You - Claim Review Submission Confirmation', // Email subject
          reply_to: 'reachus@fightformesothelioma.com', // Reply-to address
          // Template-specific fields
          full_name: contactForm.fullName || 'Valued Customer',
          name: contactForm.fullName || 'Valued Customer', // Alternative name field
          submission_date: currentDate.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          submission_time: currentDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
          }),
          // Additional fields that might be required
          date: currentDate.toLocaleDateString('en-US'),
          time: currentDate.toLocaleTimeString('en-US')
        };

        let userResult;
        try {
          // Ensure EmailJS is initialized before sending
          if (!window.emailjs.init) {
            window.emailjs.init(EMAILJS_PUBLIC_KEY);
          }

          userResult = await emailjs.send(
            EMAILJS_SERVICE_ID,
            TEMPLATE_IDS.CLAIM_REVIEW_USER,
            userTemplateParams,
            EMAILJS_PUBLIC_KEY
          );
        } catch (userError) {
          console.error('User email error details:', {
            status: userError.status,
            text: userError.text,
            message: userError.message,
            response: userError.response
          });

          // If 412 error, log template params for debugging
          if (userError.status === 412) {
            console.error('412 Precondition Failed - Template validation error');
            console.error('Template params sent:', userTemplateParams);
            console.error('Template ID:', TEMPLATE_IDS.CLAIM_REVIEW_USER);
            console.error('Service ID:', EMAILJS_SERVICE_ID);
            console.error('Error text:', userError.text);
          }

          throw userError;
        }

        if (userResult.status === 200) {
          setSubmissionStatus({
            admin: 'success',
            user: 'success',
            message: 'Both emails sent successfully!'
          });

          if (window.fbq) {
            window.fbq('track', 'Lead');
            console.log('🎯 Meta: Lead fired');
          }


          // Send data to CRM backend
          const crmResponseSuccess = await sendToCRMBackend({
            name: contactForm.fullName,
            phone: contactForm.phone,
            email: contactForm.email,
            involved: answers.involved,
            injured: answers.injured,
            fault: answers.fault,
            attorney: answers.attorney,
            period: answers.time,
            ipAddress: ipAddress,
            trustedFormCertUrl: trustedFormCertUrl,
            submissionDate: currentDate.toISOString(),
            eligibilityStatus: isEligible ? 'eligible' : 'not-eligible'
          });
          if (crmResponseSuccess?.id) {
            setCrmSubmissionId(crmResponseSuccess.id);
            setHasPhoneSubmitted(true);
          }

          // Show success dialog
          setDialogContent({
            type: 'success',
            title: 'Submission Successful!',
            message: 'Your claim review has been submitted successfully.',
            details: [
              '✓ Admin notification sent',
              '✓ Confirmation email sent',
              'We will contact you within 24 hours'
            ]
          });
          setShowDialog(true);

          // Clear the form
          setContactForm({ fullName: '', phone: '', email: '' });
        } else {
          setSubmissionStatus({
            admin: 'success',
            user: 'error',
            message: 'Admin email sent, but confirmation email failed.'
          });

          // Track Meta Pixel Lead event even for partial success (form was submitted)
          const nameParts = parseName(contactForm.fullName);
          trackMetaPixelLead({
            email: contactForm.email,
            phone: contactForm.phone,
            firstName: nameParts.firstName,
            lastName: nameParts.lastName
          });

          // Send data to CRM backend even for partial success
          const crmResponsePartial = await sendToCRMBackend({
            name: contactForm.fullName,
            phone: contactForm.phone,
            email: contactForm.email,
            involved: answers.involved,
            injured: answers.injured,
            fault: answers.fault,
            attorney: answers.attorney,
            period: answers.time,
            ipAddress: ipAddress,
            trustedFormCertUrl: trustedFormCertUrl,
            submissionDate: currentDate.toISOString(),
            eligibilityStatus: isEligible ? 'eligible' : 'not-eligible'
          });
          if (crmResponsePartial?.id) {
            setCrmSubmissionId(crmResponsePartial.id);
            setHasPhoneSubmitted(true);
          }

          setDialogContent({
            type: 'warning',
            title: 'Partially Successful',
            message: 'Admin notification sent, but confirmation email failed.',
            details: [
              '✓ Admin notification sent',
              '✗ Confirmation email failed',
              'We have received your information and will contact you soon'
            ]
          });
          setShowDialog(true);
        }
      } else {
        throw new Error('Admin email failed to send');
      }
    } catch (error) {
      console.error('Email sending error:', error);
      console.error('Error details:', {
        status: error.status,
        text: error.text,
        message: error.message,
        response: error.response
      });

      setSubmissionStatus({
        admin: submissionStatus.admin === 'success' ? 'success' : 'error',
        user: submissionStatus.user === 'sending' ? 'error' : null,
        message: submissionStatus.admin === 'success'
          ? 'Admin email sent, but confirmation email failed.'
          : 'Failed to send emails. Please try again.'
      });

      const errorMessage = error.text || error.message || 'Failed to send your information. Please try again.';
      setDialogContent({
        type: 'error',
        title: 'Submission Failed',
        message: errorMessage,
        details: error.status ? [`Status Code: ${error.status}`] : []
      });
      setShowDialog(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Inline Styles Object ---
  const styles = {
    container: {
      display: 'flex',
      height: isMobile ? 'auto' : '100vh',
      minHeight: '100vh',
      width: '100%',
      backgroundColor: '#f4f6f9',
      overflowX: 'hidden',
      fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      flexDirection: isMobile ? 'column' : 'row',
      overflowY: isMobile ? 'auto' : 'hidden'
    },
    leftSection: {
      flex: isMobile ? 'none' : 1,
      height: isMobile ? '180px' : 'auto',
      background: `url('/L01.png')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: isMobile ? 'flex-start' : 'space-between',
      padding: isMobile ? '10px 20px' : '40px 60px',
      color: 'white',
      position: 'relative',
      zIndex: 2,
    },
    brandLogo: {
      fontSize: '1.2rem',
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      letterSpacing: '1px',
    },
    topText: {
      color: '#0A1F8F',
      fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: '50px',
      fontStyle: 'normal',
      fontWeight: 800,
      lineHeight: '60px',
      textAlign: 'center',
      textTransform: 'capitalize',
      display: isMobile ? 'none' : 'block',
    },
    subtitleText: {
      color: '#162766',
      fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: '18px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '26px',
      letterSpacing: '0px',
      textAlign: 'center',
      marginTop: '10px',
      display: isMobile ? 'none' : 'block',
    },
    bottomText: {
      color: '#0A1F8F',
      fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: '30px',
      fontStyle: 'normal',
      fontWeight: 800,
      lineHeight: '40px',
      textAlign: 'center',
      textTransform: 'capitalize',
      marginBottom: '80px',
      display: isMobile ? 'none' : 'block',
    },
    rightSection: {
      flex: 1,
      display: 'flex',
      alignItems: isMobile ? 'stretch' : 'center',
      justifyContent: isMobile ? 'flex-start' : 'center',
      position: 'relative',
      padding: isMobile ? '0' : '20px',
      // Pull the white card up to overlap the top section on mobile
      marginTop: isMobile ? '-20px' : '0',
      background: isMobile ? 'transparent' : `url('/formbg2.png')`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center bottom',
      // Ensure the right section (and thus the white card) stretches to the bottom on mobile.
      // We subtract the effective visible top height (180px - 20px overlap).
      minHeight: isMobile ? 'calc(100vh - 160px)' : 'auto',
    },
    // Overlay for desktop only
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(236, 240, 245, 0.3)',
      backdropFilter: 'blur(1px)',
      zIndex: 1,
      display: isMobile ? 'none' : 'block',
    },
    formCard: {
      background: 'white',
      width: '100%',
      maxWidth: isMobile ? '100%' : '480px',
      minHeight: isMobile ? 'auto' : '520px',
      padding: isMobile ? '0 0 20px 0' : '0 40px 40px 40px',
      borderRadius: isMobile ? '20px 20px 0 0' : '16px',
      boxShadow: isMobile ? '0 -5px 20px rgba(0,0,0,0.1)' : '0 20px 40px rgba(0,0,0,0.15)',
      position: 'relative',
      zIndex: 10,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'stretch',
      flex: isMobile ? 1 : 'initial', // Makes it stretch to bottom on mobile
      overflowY: isMobile ? 'auto' : 'auto',
      overflowX: 'hidden',
    },
    stepContent: {
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'center',
      width: '100%',
      animation: 'crFadeIn 0.4s ease-out',
      paddingLeft: isMobile ? '20px' : '0',
      paddingRight: isMobile ? '20px' : '0',
      paddingTop: isMobile ? '0' : '0',
      flex: 1,
    },
    // Generic step headings (e.g. "Share Your Information to Get Started")
    h3: {
      color: '#0A1F8F',
      textAlign: 'center',
      fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: isMobile ? '20px' : '32px',
      fontStyle: 'normal',
      fontWeight: 700,
      lineHeight: 'normal',
      marginBottom: isMobile ? '15px' : '30px',
    },
    // "You may be entitled to compensation."
    subtext: {
      color: '#162766',
      textAlign: 'center',
      fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: isMobile ? '14px' : '16px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: 'normal',
      textTransform: 'capitalize',
      marginBottom: isMobile ? '15px' : '25px',
    },
    // Helper styles for future copy if needed:
    // "Take a few minutes to answer a few quick questions to see if you have a case."
    questionsIntro: {
      color: '#0A1F8F',
      textAlign: 'center',
      fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: '15px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 'normal',
      letterSpacing: '-0.2px',
      textTransform: 'capitalize',
      backgroundColor: '#E8E8F0',
      padding: '16px 20px',
      borderRadius: '8px',
      margin: '20px auto',
      width: '363px',
      maxWidth: '100%',
    },
    // "questions will be"
    questionsWillBe: {
      color: '#0A1F8F',
      fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: '28px',
      fontStyle: 'normal',
      fontWeight: 700,
      lineHeight: 'normal',
      letterSpacing: '1.12px',
      textTransform: 'capitalize',
    },
    btnPrimary: {
      backgroundColor: '#0e1e40',
      color: 'white',
      border: 'none',
      padding: isMobile ? '12px 20px' : '16px',
      width: '100%',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      marginTop: isMobile ? '5px' : '10px',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box'
    },
    optionsGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? '10px' : '15px',
      width: '100%',
      marginTop: '10px',
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#f5f5f5',
      padding: isMobile ? '12px 15px' : '16px 20px',
      borderRadius: '12px',
      cursor: 'pointer',
      textAlign: 'left',
      fontWeight: 400,
      color: '#0A1F8F',
      border: '1px solid transparent',
      transition: 'all 0.2s ease',
      boxSizing: 'border-box'
    },
    radioLabelSelected: {
      backgroundColor: '#f5f5f5',
      border: '1px solid #0A1F8F',
    },
    radioInput: {
      marginRight: '15px',
      width: '20px',
      height: '20px',
      accentColor: '#0A1F8F',
      cursor: 'pointer',
    },
    stepHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      padding: isMobile ? '12px 20px 12px 20px' : '20px 0 20px 0',
      marginBottom: '0',
      flexShrink: 0,
      order: -1,
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      color: '#0A1F8F',
      fontSize: '16px',
      fontWeight: 400,
      fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      padding: '8px 0',
    },
    stepIndicator: {
      color: '#0A1F8F',
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: 400,
      fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    },
    questionSubtitle: {
      color: '#162766',
      fontSize: '16px',
      fontWeight: 400,
      fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      textAlign: 'center',
      marginTop: '0',
      marginBottom: '30px',
      lineHeight: 'normal',
      paddingLeft: isMobile ? '0' : '0',
      paddingRight: isMobile ? '0' : '0',
    },
    input: {
      width: '100%',
      padding: '14px',
      backgroundColor: '#f1f5f9',
      border: '1px solid transparent',
      borderRadius: '8px',
      marginBottom: '12px',
      fontSize: '0.95rem',
      outline: 'none',
    },
    spinnerWrapper: {
      position: 'relative',
      width: '100px',
      height: '100px',
      margin: '0 auto 30px auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    spinnerRing: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      border: '4px solid #e2e8f0',
      borderTop: '4px solid #0e1e40',
      borderRadius: '50%',
      animation: 'crSpin 1s linear infinite',
    },
    checkboxGroup: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      margin: '15px 0 20px 0',
      fontSize: '0.7rem',
      color: '#6b7280',
      lineHeight: 1.4,
      textAlign: 'left',
    }
  };

  return (
    <div style={styles.container}>
      {/* Global CSS for Animations and Pseudo-class Hovers that inline styles can't handle */}
      <style>
        {`
          @keyframes crFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes crSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          @keyframes crDialogFadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes crDialogSlideIn { from { opacity: 0; transform: scale(0.9) translateY(-20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
          .cr-btn-hover:hover { transform: translateY(-1px); }
          .cr-radio-hover:hover { background-color: #e8e8f0 !important; }
          .cr-input-focus:focus { border-color: #0e1e40 !important; background-color: #fff !important; }
        `}
      </style>

      {/* Success/Error Dialog */}
      {showDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
            animation: 'crDialogFadeIn 0.3s ease-out',
          }}
          onClick={() => setShowDialog(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'crDialogSlideIn 0.3s ease-out',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowDialog(false)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#999',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s',
              }}
            >
              ×
            </button>

            {/* Icon */}
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              {dialogContent.type === 'success' && (
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: '#d4edda',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                  }}
                >
                  <span style={{ fontSize: '40px' }}>✓</span>
                </div>
              )}
              {dialogContent.type === 'warning' && (
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: '#fff3cd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                  }}
                >
                  <span style={{ fontSize: '40px' }}>⚠</span>
                </div>
              )}
              {dialogContent.type === 'error' && (
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: '#f8d7da',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                  }}
                >
                  <span style={{ fontSize: '40px' }}>✗</span>
                </div>
              )}
            </div>

            {/* Title */}
            <h2
              style={{
                color: '#0A1F8F',
                fontSize: '24px',
                fontWeight: 700,
                textAlign: 'center',
                marginBottom: '15px',
                fontFamily: '"Rethink Sans", system-ui, sans-serif',
              }}
            >
              {dialogContent.title}
            </h2>

            {/* Message */}
            <p
              style={{
                color: '#666',
                fontSize: '16px',
                textAlign: 'center',
                marginBottom: '20px',
                lineHeight: '1.6',
              }}
            >
              {dialogContent.message}
            </p>

            {/* Details List */}
            {dialogContent.details && dialogContent.details.length > 0 && (
              <div
                style={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '25px',
                }}
              >
                {dialogContent.details.map((detail, index) => (
                  <div
                    key={index}
                    style={{
                      color: '#333',
                      fontSize: '14px',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'flex-start',
                    }}
                  >
                    <span style={{ marginRight: '8px', flexShrink: 0 }}>
                      {detail.startsWith('✓') || detail.startsWith('✗') ? '' : '•'}
                    </span>
                    <span>{detail}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Action Button */}
            <button
              onClick={() => setShowDialog(false)}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: dialogContent.type === 'success'
                  ? '#D14836'
                  : dialogContent.type === 'warning'
                    ? '#ffc107'
                    : '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
            >
              {dialogContent.type === 'success'
                ? 'Got it, thanks!'
                : dialogContent.type === 'warning'
                  ? 'Understood'
                  : 'Try Again'}
            </button>
          </div>
        </div>
      )}

      {/* LEFT SECTION */}
      <div style={styles.leftSection}>
        {/* <div style={styles.brandLogo}>
          <svg viewBox="0 0 24 24" style={{ fill: '#e74c3c', width: '24px', height: '24px' }}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
          </svg>
          CCRL
        </div> */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <img src={nimg1} alt="Logo" style={{ width: '10rem', height: '3rem', flexShrink: 0, objectFit: 'contain', margin: '0px', alignSelf: 'auto' }} />
          {isMobile && (
            <img src={BMNLogo} alt="BMN Logo" style={{ width: '130px', maxWidth: '130px', height: 'auto' }} />
          )}
        </div>
        {/* Top Text - Left aligned */}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', width: '100%', marginTop: '40px' }}>
          {!isMobile && (
            <h2 style={{
              color: '#0A1F8F',
              fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              fontSize: '50px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: '60px',
              textAlign: 'left'
            }}>Don't Leave<br />Compensation Unclaimed</h2>
          )}
          {!isMobile && (
            <p style={{
              color: '#D14836',
              fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              fontSize: '20px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: 'normal',
              textAlign: 'left'
            }}>Check if you're eligible after a motor vehicle accident.</p>
          )}
        </div>
        {/* Spacer to push bottom text down */}
        <div style={{ flex: 1 }}></div>
        {/* Bottom Text - Centered at bottom of image */}
        {/* <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <h2 style={styles.bottomText}>Check If You're Eligible - It Only Takes A Minute.</h2>
        </div> */}
      </div>

      {/* RIGHT SECTION */}
      <div style={styles.rightSection}>
        {/* Overlay simulated div */}
        <div style={styles.overlay}></div>

        {/* BMN Logo - Desktop only, positioned at top right */}
        {!isMobile && (
          <img
            src={BMNLogo}
            alt="BMN Logo"
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              width: 'auto',
              maxWidth: '200px',
              height: 'auto',
              zIndex: 20
            }}
          />
        )}

        <div style={styles.formCard}>
          {/* Logo */}
          {/* <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <img src="/CLAIM4ACCIDENTS.png" alt="CLAIM4ACCIDENTS" style={{ maxWidth: '200px', height: 'auto' }} />
          </div>
           */}

          {/* Step Header - Show for question steps (1-5) and contact form (8) */}
          {((step >= 1 && step <= 5) || step === 8) && (
            <div style={styles.stepHeader}>
              <button
                style={styles.backButton}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (step === 8) {
                    // On the contact form, "Back" should return to the previous form step
                    // (not the eligibility/congratulations overlay).
                    setStep(5);
                  } else {
                    setStep(step - 1);
                  }
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="#0A1F8F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Back
              </button>
              <span style={styles.stepIndicator}>Step {step}/8</span>
            </div>
          )}

          {/* STEP 0: Landing */}
          {step === 0 && (
            <div style={{
              ...styles.stepContent,
              paddingTop: isMobile ? '50px' : '40px',
              justifyContent: 'center',
              minHeight: isMobile ? 'auto' : '480px'
            }}>
              <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <img src="/CCRINITIAL.svg" alt="CLAIM4ACCIDENTS" style={{ maxWidth: '200px', height: 'auto' }} />
              </div>
              <h3 style={styles.h3}>Share Your Information to Get Started</h3>
              <p style={{
                color: '#0A1F8F',
                textAlign: 'center',
                fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                fontSize: isMobile ? '14px' : '16px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: 'normal',
                textTransform: 'capitalize',
                marginBottom: isMobile ? '15px' : '25px',
              }}>You may be entitled to compensation.</p>
              <div style={styles.questionsIntro}>Take a few minutes to answer a few quick questions to see if you have a case.</div>
              <button
                className="cr-btn-hover"
                style={{
                  display: 'flex',
                  width: '363px',
                  height: '50px',
                  padding: '10px 20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '10px',
                  background: '#D14836',
                  color: 'white',
                  border: 'none',
                  fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  fontSize: '20px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                  textAlign: 'right',
                  cursor: 'pointer',
                  marginTop: '10px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  transition: 'all 0.3s ease',
                }}
                onClick={() => {

                  // Track Google Ads Sign-Up conversion
                  if (typeof window !== 'undefined' && window.gtag_report_conversion) {
                    window.gtag_report_conversion();
                  }
                  if (window.fbq) {
                    window.fbq('track', 'CompleteRegistration');
                    console.log('🎯 Meta: CompleteRegistration fired');
                  }
                  setStep(1);
                }}
              >
                Start Your Claim <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '8px', verticalAlign: 'middle' }}>
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="white" strokeWidth="1.73177" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          )}

          {/* STEP 1: Question 1 */}
          {step === 1 && (
            <div style={styles.stepContent}>
              <p style={styles.questionSubtitle}>Motor Vehicle Accident Free Case Review</p>
              <h3 style={styles.h3}>Were you involved in a Motor Vehicle Accident?</h3>
              <div style={styles.optionsGroup}>
                <label
                  className="cr-radio-hover"
                  style={{
                    ...styles.radioLabel,
                    ...(answers.involved === 'yes' ? styles.radioLabelSelected : {})
                  }}
                  onClick={() => handleAnswer('involved', 'yes')}
                >
                  <input
                    type="radio"
                    style={styles.radioInput}
                    name="involved"
                    value="yes"
                    checked={answers.involved === 'yes'}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleAnswer('involved', 'yes');
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAnswer('involved', 'yes');
                    }}
                  />
                  Yes
                </label>
                <label
                  className="cr-radio-hover"
                  style={{
                    ...styles.radioLabel,
                    ...(answers.involved === 'no' ? styles.radioLabelSelected : {})
                  }}
                  onClick={() => handleAnswer('involved', 'no')}
                >
                  <input
                    type="radio"
                    style={styles.radioInput}
                    name="involved"
                    value="no"
                    checked={answers.involved === 'no'}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleAnswer('involved', 'no');
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAnswer('involved', 'no');
                    }}
                  />
                  No
                </label>
              </div>
            </div>
          )}

          {/* STEP 2: Question 2 */}
          {step === 2 && (
            <div style={styles.stepContent}>
              <p style={styles.questionSubtitle}>Motor Vehicle Accident Free Case Review</p>
              <h3 style={{
                color: '#0A1F8F',
                textAlign: 'center',
                fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                fontSize: isMobile ? '20px' : '28px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
                marginBottom: isMobile ? '15px' : '30px',
              }}>Were you injured in a motor vehicle accident?</h3>
              <div style={styles.optionsGroup}>
                <label
                  className="cr-radio-hover"
                  style={{
                    ...styles.radioLabel,
                    ...(answers.injured === 'yes' ? styles.radioLabelSelected : {})
                  }}
                  onClick={() => handleAnswer('injured', 'yes')}
                >
                  <input
                    type="radio"
                    style={styles.radioInput}
                    name="injured"
                    value="yes"
                    checked={answers.injured === 'yes'}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleAnswer('injured', 'yes');
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAnswer('injured', 'yes');
                    }}
                  />
                  Yes
                </label>
                <label
                  className="cr-radio-hover"
                  style={{
                    ...styles.radioLabel,
                    ...(answers.injured === 'no' ? styles.radioLabelSelected : {})
                  }}
                  onClick={() => handleAnswer('injured', 'no')}
                >
                  <input
                    type="radio"
                    style={styles.radioInput}
                    name="injured"
                    value="no"
                    checked={answers.injured === 'no'}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleAnswer('injured', 'no');
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAnswer('injured', 'no');
                    }}
                  />
                  No
                </label>
              </div>
            </div>
          )}

          {/* STEP 3: Question 3 */}
          {step === 3 && (
            <div style={styles.stepContent}>
              <p style={styles.questionSubtitle}>Motor Vehicle Accident Free Case Review</p>
              <h3 style={styles.h3}>Were You Legally At Fault?</h3>
              <div style={styles.optionsGroup}>
                <label
                  className="cr-radio-hover"
                  style={{
                    ...styles.radioLabel,
                    ...(answers.fault === 'yes' ? styles.radioLabelSelected : {})
                  }}
                  onClick={() => handleAnswer('fault', 'yes')}
                >
                  <input
                    type="radio"
                    style={styles.radioInput}
                    name="fault"
                    value="yes"
                    checked={answers.fault === 'yes'}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleAnswer('fault', 'yes');
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAnswer('fault', 'yes');
                    }}
                  />
                  Yes
                </label>
                <label
                  className="cr-radio-hover"
                  style={{
                    ...styles.radioLabel,
                    ...(answers.fault === 'no' ? styles.radioLabelSelected : {})
                  }}
                  onClick={() => handleAnswer('fault', 'no')}
                >
                  <input
                    type="radio"
                    style={styles.radioInput}
                    name="fault"
                    value="no"
                    checked={answers.fault === 'no'}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleAnswer('fault', 'no');
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAnswer('fault', 'no');
                    }}
                  />
                  No
                </label>
              </div>
            </div>
          )}

          {/* STEP 4: Question 4 */}
          {step === 4 && (
            <div style={styles.stepContent}>
              <p style={styles.questionSubtitle}>Motor Vehicle Accident Free Case Review</p>
              <h3 style={styles.h3}>Do You Already Have An Attorney?</h3>
              <div style={styles.optionsGroup}>
                <label
                  className="cr-radio-hover"
                  style={{
                    ...styles.radioLabel,
                    ...(answers.attorney === 'yes' ? styles.radioLabelSelected : {})
                  }}
                  onClick={() => handleAnswer('attorney', 'yes')}
                >
                  <input
                    type="radio"
                    style={styles.radioInput}
                    name="attorney"
                    value="yes"
                    checked={answers.attorney === 'yes'}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleAnswer('attorney', 'yes');
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAnswer('attorney', 'yes');
                    }}
                  />
                  Yes
                </label>
                <label
                  className="cr-radio-hover"
                  style={{
                    ...styles.radioLabel,
                    ...(answers.attorney === 'no' ? styles.radioLabelSelected : {})
                  }}
                  onClick={() => handleAnswer('attorney', 'no')}
                >
                  <input
                    type="radio"
                    style={styles.radioInput}
                    name="attorney"
                    value="no"
                    checked={answers.attorney === 'no'}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleAnswer('attorney', 'no');
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAnswer('attorney', 'no');
                    }}
                  />
                  No
                </label>
              </div>
            </div>
          )}

          {/* STEP 5: Question 5 */}
          {step === 5 && (
            <div style={styles.stepContent}>
              <p style={styles.questionSubtitle}>Motor Vehicle Accident Free Case Review</p>
              <h3 style={styles.h3}>When Did The Accident Happen?</h3>
              <div style={styles.optionsGroup}>
                {['In last 1 month', 'In last 3 months', 'In last 6 months', 'In last 1 year'].map((timeOption) => (
                  <label
                    key={timeOption}
                    className="cr-radio-hover"
                    style={{
                      ...styles.radioLabel,
                      textTransform: 'capitalize',
                      ...(answers.time === timeOption ? styles.radioLabelSelected : {})
                    }}
                    onClick={() => handleAnswer('time', timeOption)}
                  >
                    <input
                      type="radio"
                      style={styles.radioInput}
                      name="time"
                      value={timeOption}
                      checked={answers.time === timeOption}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleAnswer('time', timeOption);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAnswer('time', timeOption);
                      }}
                    />
                    {timeOption}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* STEP 6: Spinner */}
          {step === 6 && (
            <div style={{ ...styles.stepContent, justifyContent: 'center', minHeight: isMobile ? 'auto' : '400px' }}>
              <div style={styles.spinnerWrapper}>
                <div style={styles.spinnerRing}></div>
                <img src='EC.png' alt='EC' style={{ width: '40px', height: '40px' }} />
              </div>
              <div style={{ color: '#0e1e40', fontWeight: 700, fontSize: '1.1rem', marginBottom: '5px' }}>Checking Your Eligibility.</div>
              <div style={{ fontSize: '0.9rem', color: '#666' }}>This Should Take Less Than A Minute.</div>
            </div>
          )}

          {/* STEP 7: Eligibility Success Screen */}
          {step === 7 && (
            <div style={{ ...styles.stepContent, justifyContent: 'center', minHeight: isMobile ? 'auto' : '400px' }}>
              {/* Green Checkmark Circle */}
              <div style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                backgroundColor: '#4CAF50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 30px auto',
                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
              }}>
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>

              {/* Congratulations Text */}
              <h2 style={{
                color: '#D14836',
                textAlign: 'center',
                fontFamily: 'Raleway, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                fontSize: '25px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '35px',
                letterSpacing: '1px',
                marginBottom: '10px'
              }}>
                Congratulations!
              </h2>

              {/* You're Eligible Text */}
              <h2 style={{
                color: '#0A1F8F',
                fontFamily: 'Raleway, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                fontSize: '25px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '35px',
                letterSpacing: '1px',
                textAlign: 'center',
                marginBottom: '20px'
              }}>
                You're eligible!
              </h2>

              {/* Description Text */}
              <p style={{
                color: '#0A1F8F',
                textAlign: 'center',
                fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                marginBottom: '40px',
                maxWidth: '400px',
                margin: '0 auto 40px auto'
              }}>
                Please provide your details so we can review your options and proceed.
              </p>

              {/* Button with Spinner and Please Wait */}
              <button
                disabled
                style={{
                  display: 'flex',
                  width: isMobile ? '100%' : '363px',
                  maxWidth: '100%',
                  height: '50px',
                  padding: '10px 20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '10px',
                  background: 'rgba(209, 72, 54, 0.20)',
                  color: 'white',
                  border: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'not-allowed',
                  marginTop: '10px',
                  gap: '12px',
                  opacity: 0.9
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '3px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '3px solid white',
                  borderRadius: '50%',
                  animation: 'crSpin 1s linear infinite'
                }}></div>
                <span>Please wait...</span>
              </button>
            </div>
          )}

          {/* STEP 8: Contact Form */}
          {step === 8 && (
            <div style={{ ...styles.stepContent, justifyContent: 'flex-start', paddingTop: isMobile ? '20px' : '20px' }}>
              <div style={{ textAlign: 'left' }}>
                <h3 style={{ ...styles.h3, textAlign: 'left', fontSize: '1.25rem' }}>Share Your Information to Get Started</h3>
                {/* <p style={{...styles.subtext, textAlign: 'left', fontSize: '0.85rem', color: '#162766', fontWeight: 600, marginBottom: '20px'}}>You May Be Entitled To Settlement.</p> */}

                <form onSubmit={handleContactSubmit}>
                  {/* Full Name */}
                  <input
                    type="text"
                    className="cr-input-focus"
                    style={styles.input}
                    placeholder="Full Name"
                    value={contactForm.fullName}
                    onChange={(e) => {
                      // allow only letters, spaces, hyphens and apostrophes (strip numbers and other chars)
                      const sanitized = e.target.value.replace(/[^A-Za-z' -]/g, '');
                      setContactForm({ ...contactForm, fullName: sanitized });
                      setContactErrors({ ...contactErrors, fullName: '' });
                    }}
                    required
                  />
                  {contactErrors.fullName && (
                    <div style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '8px', marginTop: '-8px' }}>
                      {contactErrors.fullName}
                    </div>
                  )}

                  {/* Phone Number */}
                  <input
                    type="tel"
                    className="cr-input-focus"
                    style={styles.input}
                    placeholder="Phone Number"
                    value={contactForm.phone}
                    onChange={(e) => {
                      // allow only digits and limit to 10 characters
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setContactForm({ ...contactForm, phone: digits });
                      setContactErrors({ ...contactErrors, phone: '' });
                      // Only trigger submission when exactly 10 digits are reached
                      if (digits.length === 10) {
                        triggerPhoneSubmission(digits);
                      }
                    }}
                    maxLength={10}
                    required
                  />
                  {contactErrors.phone && (
                    <div style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '8px', marginTop: '-8px' }}>
                      {contactErrors.phone}
                    </div>
                  )}

                  {/* Email Address */}
                  <input
                    type="email"
                    className="cr-input-focus"
                    style={styles.input}
                    placeholder="Email Address"
                    value={contactForm.email}
                    onChange={(e) => {
                      const value = e.target.value;
                      setContactForm({ ...contactForm, email: value });
                      setContactErrors({ ...contactErrors, email: '' });
                      triggerEmailUpdate(value);
                    }}
                    required
                  />
                  {contactErrors.email && (
                    <div style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '8px', marginTop: '-8px' }}>
                      {contactErrors.email}
                    </div>
                  )}

                  <div style={styles.checkboxGroup}>
                    <input type="checkbox" id="tcpa" required style={{ marginTop: '2px' }} />
                    <label htmlFor="tcpa" style={{
                      color: '#757575',
                      fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      fontSize: '10px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: 'normal',
                      letterSpacing: '-0.2px'
                    }}>
                      I agree to the{' '}
                      <a
                        href="/PrivacyPolicy"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#D14836',
                          fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                          fontSize: '10px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: 'normal',
                          letterSpacing: '-0.2px',
                          textDecorationLine: 'underline',
                          textDecorationStyle: 'solid',
                          textDecorationSkipInk: 'auto',
                          textDecorationThickness: 'auto',
                          textUnderlineOffset: 'auto',
                          textUnderlinePosition: 'from-font'
                        }}
                      >
                        Privacy policy
                      </a>
                      {' '}&{' '}
                      <a
                        href="/Disclaimer"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#D14836',
                          fontFamily: '"Rethink Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                          fontSize: '10px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: 'normal',
                          letterSpacing: '-0.2px',
                          textDecorationLine: 'underline',
                          textDecorationStyle: 'solid',
                          textDecorationSkipInk: 'auto',
                          textDecorationThickness: 'auto',
                          textUnderlineOffset: 'auto',
                          textUnderlinePosition: 'from-font'
                        }}
                      >
                        Disclaimer
                      </a>
                      {' '}& give my express written consent, affiliates & /or lawyer to contact you at the number provided above, even if this number is a wireless number or if I am presently listed on a Do Not Call list. I understand that I may be contacted by telephone, email, text message or mail regarding case options & that I may be called using automatic dialing equipment. Message & data rates may apply. My consent does not require purchase. This is Legal advertising.
                    </label>
                  </div>

                  {/* Submission Status Message */}
                  {submissionStatus.message && (
                    <div style={{
                      padding: '12px',
                      marginBottom: '10px',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      textAlign: 'center',
                      backgroundColor: submissionStatus.admin === 'success' && submissionStatus.user === 'success'
                        ? '#d4edda'
                        : submissionStatus.admin === 'error'
                          ? '#f8d7da'
                          : '#d1ecf1',
                      color: submissionStatus.admin === 'success' && submissionStatus.user === 'success'
                        ? '#155724'
                        : submissionStatus.admin === 'error'
                          ? '#721c24'
                          : '#0c5460',
                      border: `1px solid ${submissionStatus.admin === 'success' && submissionStatus.user === 'success'
                        ? '#c3e6cb'
                        : submissionStatus.admin === 'error'
                          ? '#f5c6cb'
                          : '#bee5eb'}`
                    }}>
                      {submissionStatus.admin === 'sending' && '📧 Sending admin notification...'}
                      {submissionStatus.admin === 'success' && submissionStatus.user === 'sending' && '✅ Admin sent! Sending confirmation...'}
                      {submissionStatus.admin === 'success' && submissionStatus.user === 'success' && '✅ Both emails sent successfully!'}
                      {submissionStatus.admin === 'error' && '❌ Failed to send. Please try again.'}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="cr-btn-hover"
                    disabled={isSubmitting}
                    style={{
                      display: 'flex',
                      width: isMobile ? '100%' : '363px',
                      maxWidth: '100%',
                      height: '50px',
                      padding: '10px 20px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: '10px',
                      background: isSubmitting ? '#999' : '#D14836',
                      color: 'white',
                      border: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      marginTop: '10px',
                      transition: 'all 0.3s ease',
                      opacity: isSubmitting ? 0.7 : 1,
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <span style={{ marginRight: '8px' }}>⏳</span>
                        Sending...
                      </>
                    ) : (
                      <>Submit Now &rarr;</>
                    )}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* STEP 9: Not Eligible */}
          {step === 9 && (
            <div style={{ ...styles.stepContent, justifyContent: 'center', minHeight: isMobile ? 'auto' : '400px' }}>
              {/* <svg style={{ width: '80px', height: '80px', margin: '0 auto 20px auto' }} viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="45" style={{ stroke: '#ef4444', strokeWidth: 3, fill: 'none' }} />
                <path d="M35 35 L65 65 M65 35 L35 65" style={{ stroke: '#ef4444', strokeWidth: 3, strokeLinecap: 'round' }} />
              </svg> */}
              <img src='NEC.png' alt='NEC' style={{ width: '80px', height: '80px', margin: '0 auto 20px auto' }} />

              <div style={{ color: '#ef4444', fontWeight: 700, fontSize: '1.4rem', marginBottom: '15px' }}>You're Not Eligible!</div>
              <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '30px' }}>
                You're Not Eligible For This Claim, But You May Qualify For Other Assistance Options.
              </p>
              <button className="cr-btn-hover" style={{ ...styles.btnPrimary, backgroundColor: '#666' }} onClick={resetForm}>Return Home</button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ClaimReview;